import { useEffect } from "react";
import { useGetPublicBrandingQuery } from "../Api/attoDeskApi";
import { IPublicBranding } from "../Api/Interface/api.interface";

const CACHE_KEY = "branding";

/**
 * Portal branding from Settings → Business Profile.
 *
 * The endpoint is public, so the login page can call it before anyone signs
 * in. The last answer is cached in localStorage and returned immediately on
 * the next load — otherwise every page load would show the built-in logo for
 * a moment and then swap it for the uploaded one.
 *
 * Every field may be null: nothing uploaded means the caller keeps its own
 * static image, which is what an install with no Business Profile logos gets.
 */
export const useBranding = (): Partial<IPublicBranding> => {
  const { data } = useGetPublicBrandingQuery();
  const branding = (data as any)?.data as IPublicBranding | undefined;

  useEffect(() => {
    if (!branding) return;
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(branding));
    } catch {
      /* private mode / quota — the app works without the cache */
    }
  }, [branding]);

  const cached = (() => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      return raw ? (JSON.parse(raw) as IPublicBranding) : undefined;
    } catch {
      return undefined;
    }
  })();

  const resolved: Partial<IPublicBranding> = branding || cached || {};

  // Keep the browser tab icon in step with the uploaded favicon.
  useEffect(() => {
    const href = resolved.favicon_url;
    if (!href) return;
    document.querySelectorAll<HTMLLinkElement>("link[rel~='icon'], link[rel='apple-touch-icon']")
      .forEach((link) => { link.href = href; });
  }, [resolved.favicon_url]);

  return resolved;
};

export default useBranding;
